/**
 * Glitch Prism Grid Visualizer - Perspective grid floor with glitching prism rectangles
 *
 * A receding grid of geometric blocks in perspective lanes, with beat-triggered
 * horizontal tear glitches and treble-driven chromatic aberration. Designed to be
 * readable from far away (stage/performance context).
 *
 * Uses offscreen canvas trail technique that properly respects the global background
 * controller (solid, gradient, pattern, or image backgrounds).
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Perspective grid lines — converging lanes with horizontal depth rows
 * 3. Prism blocks — back-to-front depth-ordered rectangles with audio-driven height
 * 4. Chromatic aberration — RGB-split blocks on high treble energy
 * 5. Glow — shadowBlur applied around block rendering
 *
 * Audio mapping:
 * - Bass (subBass + bass) → grid wobble + height boost on low lanes
 * - Mids → block density (more mid = lower threshold = more visible blocks)
 * - Treble (highEnergy) → chromatic aberration probability and offset distance
 * - Beat → horizontal tear glitches on random row groups
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'glitch-prism-grid',
  displayName: 'Glitch Prism Grid',
  contextType: '2d',
};

// Glitch decay factor — controls how quickly tear offsets settle
const GLITCH_DECAY = 0.75;
// Maximum glitch duration in frames
const GLITCH_MAX_FRAMES = 12;

class GlitchPrismGrid extends Base2DVisualizer {
  static name = 'glitch-prism-grid';
  static displayName = 'Glitch Prism Grid';

  static getConfigUI() {
    return [
      { type: 'range', id: 'perspective', label: 'Perspective', min: 0, max: 100, default: 100, scale: 0.01 },
      { type: 'range', id: 'laneCount', label: 'Lane Count', min: 4, max: 24, default: 12 },
      { type: 'range', id: 'gridDepth', label: 'Grid Depth', min: 4, max: 20, default: 10 },
      { type: 'range', id: 'blockDensity', label: 'Block Density', min: 10, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'glitchAmount', label: 'Glitch Amount', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'chromaOffset', label: 'Chroma Offset', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'wobble', label: 'Bass Wobble', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 40, default: 15 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 5, max: 40, default: 12, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 70, scale: 0.01 },
      { type: 'checkbox', id: 'glow', label: 'Enable Glow', default: true },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#00ffcc' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#ff00aa' },
      { type: 'color', id: 'gridColor', label: 'Grid Line Color', default: '#1a3a5c' },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return this.config.laneCount;
  }

  init() {
    this._isFirstFrame = true;

    // Trail offscreen canvas
    this._trailCanvas = null;
    this._trailCtx = null;

    // Resize detection
    this._cachedWidth = 0;
    this._cachedHeight = 0;

    // Grid geometry cache
    this._gridCache = null;

    // Wobble phase accumulator
    this._wobblePhase = 0;

    // Row glitch state — pre-allocated array
    this._rowGlitch = [];
    this._initRowGlitch();

    // Color cache (parsed RGB from hex for lerp)
    this._csR = 0; this._csG = 0; this._csB = 0;
    this._ceR = 0; this._ceG = 0; this._ceB = 0;
    this._cachedCS = '';
    this._cachedCE = '';

    // Compute initial grid if canvas has dimensions
    if (this.canvas.width > 0 && this.canvas.height > 0) {
      this._computeGrid();
      this._cachedWidth = this.canvas.width;
      this._cachedHeight = this.canvas.height;
    }
  }

  onConfigChange(changed) {
    if (changed.laneCount !== undefined || changed.gridDepth !== undefined) {
      this._computeGrid();
      this._initRowGlitch();
      this.previousAmplitudes = new Array(this.config.laneCount).fill(0);
    } else if (changed.perspective !== undefined) {
      this._computeGrid();
    }
  }

  /**
   * Initialize row glitch state array.
   */
  _initRowGlitch() {
    const depth = this.config.gridDepth;
    this._rowGlitch = new Array(depth);
    for (let d = 0; d < depth; d++) {
      this._rowGlitch[d] = { offsetX: 0, framesLeft: 0, decay: GLITCH_DECAY };
    }
  }

  /**
   * Ensure the trail offscreen canvas matches the main canvas dimensions.
   */
  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  /**
   * Precompute grid geometry, interpolating between flat and perspective layouts.
   *
   * The `perspective` config (0–1) controls the morph:
   * - perspective=1: Vanishing point at top-center, converging lanes, power-curve rows
   * - perspective=0: Parallel vertical lanes filling the full canvas, evenly-spaced rows
   *
   * Interpolation strategy:
   * - Row spacing: lerp between linear t and power-curve t^1.5
   * - Lane convergence factor: (1 - depthT * p) — at p=0 lanes stay full width
   * - Top edge margin: lerp between 3% (flat) and 15% (perspective)
   */
  _computeGrid() {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const { laneCount, gridDepth, perspective: p } = this.config;

    // Center X (also VP X in perspective mode)
    const vpX = w / 2;

    // Top and bottom margins interpolate with perspective:
    // Flat mode uses nearly full canvas; perspective mode shrinks to VP area
    const topMarginFraction = 0.03 + p * 0.12;   // 0.03 (flat) → 0.15 (perspective)
    const bottomMarginFraction = 0.95 + (1 - p) * 0.02; // 0.95 (persp) → 0.97 (flat)
    const vpY = h * topMarginFraction;
    const bottomY = h * bottomMarginFraction;
    const totalHeight = bottomY - vpY;

    // Horizontal lines: lerp between linear and power-curve spacing
    const hLines = new Array(gridDepth + 1);
    for (let d = 0; d <= gridDepth; d++) {
      const ratio = d / gridDepth;
      const tLinear = ratio;
      const tPerspective = Math.pow(ratio, 1.5);
      const t = tLinear + (tPerspective - tLinear) * p;
      hLines[d] = bottomY - t * totalHeight;
    }

    // Cell geometry: flat array [depth * laneCount]
    const cellGeometry = new Array(gridDepth * laneCount);
    const spread = w * 0.95;

    for (let d = 0; d < gridDepth; d++) {
      const y0 = hLines[d];       // Near edge (bottom of cell)
      const y1 = hLines[d + 1];   // Far edge (top of cell)
      const screenY = (y0 + y1) / 2;
      const cellHeight = y0 - y1;

      // Depth fractions for convergence (0 at bottom, 1 at top)
      const tNear = (bottomY - y0) / totalHeight;
      const tFar = (bottomY - y1) / totalHeight;

      // Convergence factor: at p=0 (flat) = 1 (no narrowing), at p=1 = (1-depthT)
      const convNear = 1 - tNear * p;
      const convFar = 1 - tFar * p;
      const convMid = (convNear + convFar) / 2;

      for (let lane = 0; lane < laneCount; lane++) {
        const laneT0 = lane / laneCount;
        const laneT1 = (lane + 1) / laneCount;
        const laneMid = (laneT0 + laneT1) / 2;

        // Lane positions with convergence applied
        const nearLeft = vpX + (laneT0 - 0.5) * spread * convNear;
        const nearRight = vpX + (laneT1 - 0.5) * spread * convNear;
        const farLeft = vpX + (laneT0 - 0.5) * spread * convFar;
        const farRight = vpX + (laneT1 - 0.5) * spread * convFar;

        const centerX = vpX + (laneMid - 0.5) * spread * convMid;
        const cellWidth = ((nearRight - nearLeft) + (farRight - farLeft)) / 2;

        const maxBlockHeight = cellHeight * 0.85;

        cellGeometry[d * laneCount + lane] = {
          centerX,
          cellWidth,
          screenY,
          maxBlockHeight,
          nearY: y0,
          farY: y1,
          nearLeft,
          nearRight,
          farLeft,
          farRight,
        };
      }
    }

    this._gridCache = {
      vpX,
      vpY,
      bottomY,
      totalHeight,
      hLines,
      cellGeometry,
      laneCount,
      gridDepth,
      perspective: p,
    };
  }

  /**
   * Parse hex color to RGB components and cache.
   */
  _ensureColorCache() {
    if (this._cachedCS !== this.config.colorStart) {
      this._cachedCS = this.config.colorStart;
      const c = this._cachedCS;
      this._csR = parseInt(c.slice(1, 3), 16) || 0;
      this._csG = parseInt(c.slice(3, 5), 16) || 0;
      this._csB = parseInt(c.slice(5, 7), 16) || 0;
    }
    if (this._cachedCE !== this.config.colorEnd) {
      this._cachedCE = this.config.colorEnd;
      const c = this._cachedCE;
      this._ceR = parseInt(c.slice(1, 3), 16) || 0;
      this._ceG = parseInt(c.slice(3, 5), 16) || 0;
      this._ceB = parseInt(c.slice(5, 7), 16) || 0;
    }
  }

  /**
   * Interpolate between colorStart and colorEnd using cached RGB.
   * Returns an rgb() string.
   */
  _laneColor(t) {
    const r = Math.round(this._csR + (this._ceR - this._csR) * t);
    const g = Math.round(this._csG + (this._ceG - this._csG) * t);
    const b = Math.round(this._csB + (this._ceB - this._csB) * t);
    return `rgb(${r},${g},${b})`;
  }

  /**
   * Get separate R, G, B channels for chromatic aberration.
   */
  _laneColorChannels(t) {
    return {
      r: Math.round(this._csR + (this._ceR - this._csR) * t),
      g: Math.round(this._csG + (this._ceG - this._csG) * t),
      b: Math.round(this._csB + (this._ceB - this._csB) * t),
    };
  }

  /**
   * Override draw() for offscreen canvas trail technique.
   * Same pattern as laser-fan-rig.js — properly respects the background controller.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters (bassEnergy, beatDetected, etc.)
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    // Check for resize → recompute grid (guard against zero dimensions)
    if (canvas.width !== this._cachedWidth || canvas.height !== this._cachedHeight) {
      this._cachedWidth = canvas.width;
      this._cachedHeight = canvas.height;
      if (canvas.width > 0 && canvas.height > 0) {
        this._computeGrid();
      }
    }

    if (this._isFirstFrame) {
      this.clearCanvas();
      this._isFirstFrame = false;
    } else {
      // Trail effect via offscreen canvas compositing
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  /**
   * Main render: update glitches, draw grid, draw prism blocks.
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const grid = this._gridCache;
    if (!grid) return;

    const { laneCount, gridDepth } = grid;
    this._ensureColorCache();

    // --- Decay existing row glitches ---
    for (let d = 0; d < gridDepth; d++) {
      const g = this._rowGlitch[d];
      if (g.framesLeft > 0) {
        g.offsetX *= g.decay;
        g.framesLeft--;
        if (g.framesLeft <= 0) g.offsetX = 0;
      }
    }

    // --- Beat detection → trigger new glitches ---
    if (this.beatDetected && config.glitchAmount > 0) {
      const numTears = 1 + Math.floor(Math.random() * 3); // 1-3 tears
      for (let t = 0; t < numTears; t++) {
        const startRow = Math.floor(Math.random() * gridDepth);
        const span = 1 + Math.floor(Math.random() * 3);
        const maxOffset = config.glitchAmount * canvas.width * 0.15;
        const offset = (Math.random() - 0.5) * 2 * maxOffset * (0.5 + this.beatConfidence * 0.5);

        for (let d = startRow; d < Math.min(startRow + span, gridDepth); d++) {
          this._rowGlitch[d].offsetX = offset;
          this._rowGlitch[d].framesLeft = 4 + Math.floor(Math.random() * (GLITCH_MAX_FRAMES - 4));
          this._rowGlitch[d].decay = GLITCH_DECAY + Math.random() * 0.1;
        }
      }
    }

    // --- Compute wobble offset from bass energy ---
    const bassTotal = this.subBassEnergy + this.bassEnergy;
    this._wobblePhase += 0.05 + bassTotal * 0.15;
    // Wrap phase to prevent floating-point precision loss in long sessions
    if (this._wobblePhase > 6283) this._wobblePhase -= 6283; // ~1000 * 2π
    const wobbleOffset = Math.sin(this._wobblePhase) * bassTotal * config.wobble * canvas.width * 0.02;

    // --- Draw perspective grid lines (with per-row glitch offsets for CRT tear) ---
    this._drawGrid(ctx, grid, wobbleOffset, this._rowGlitch);

    // --- Draw prism blocks back-to-front ---
    const densityThreshold = 1 - config.blockDensity;
    const midBoost = this.midEnergy;
    const effectiveThreshold = densityThreshold * (1 - midBoost * 0.4);

    // Chromatic aberration parameters
    const highE = this.highEnergy;
    const chromaProb = highE * config.chromaOffset;
    const chromaDist = 2 + highE * 6 * config.chromaOffset;
    let chromaCount = 0;
    const maxChroma = Math.ceil(gridDepth * laneCount * 0.3);

    // Set up glow once
    if (config.glow && config.glowIntensity > 0) {
      ctx.shadowBlur = config.glowIntensity;
    }

    // Render back-to-front (farthest row first)
    for (let d = gridDepth - 1; d >= 0; d--) {
      const glitchX = this._rowGlitch[d].offsetX;

      for (let lane = 0; lane < laneCount; lane++) {
        const amp = amplitudes[lane];

        // Depth falloff
        const depthFalloff = 1 - (d / gridDepth) * 0.6;
        const effectiveAmp = amp * depthFalloff;

        // Skip if below density threshold
        if (effectiveAmp < effectiveThreshold) continue;

        const cell = grid.cellGeometry[d * laneCount + lane];
        const laneT = laneCount > 1 ? lane / (laneCount - 1) : 0;

        // Block dimensions
        const blockWidth = cell.cellWidth * 0.7;
        const blockHeight = cell.maxBlockHeight * effectiveAmp;
        const x = cell.centerX + wobbleOffset + glitchX - blockWidth / 2;
        const y = cell.screenY - blockHeight;

        // Determine if chromatic or normal draw
        const doChroma = chromaCount < maxChroma && chromaProb > 0 && Math.random() < chromaProb;

        if (doChroma) {
          chromaCount++;
          this._drawChromaticBlock(ctx, x, y, blockWidth, blockHeight, laneT, chromaDist, effectiveAmp);
        } else {
          // Normal block
          const color = this._laneColor(laneT);
          if (config.glow && config.glowIntensity > 0) {
            ctx.shadowColor = color;
          }
          ctx.globalAlpha = 0.6 + effectiveAmp * 0.4;
          ctx.fillStyle = color;
          ctx.fillRect(x, y, blockWidth, blockHeight);
        }
      }
    }

    // Reset glow and alpha
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }

  /**
   * Draw the grid lines with wobble and per-row glitch offsets.
   * Horizontal lines shift with their row's glitch offset for a CRT tear effect.
   * Vertical lines interpolate between converging (perspective) and parallel (flat).
   */
  _drawGrid(ctx, grid, wobbleOffset, rowGlitch) {
    const { vpX, vpY, bottomY, hLines, laneCount, gridDepth, perspective: p } = grid;
    const w = this.canvas.width;
    const gridColor = this.config.gridColor;
    const spread = w * 0.95;

    ctx.save();
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.4;

    // Horizontal depth lines (with per-row glitch offset)
    for (let d = 0; d <= gridDepth; d++) {
      const y = hLines[d];
      const t = (bottomY - y) / grid.totalHeight;
      // Line width: lerp between full spread (flat) and converging spread (perspective)
      const lineSpread = spread * (1 - t * p);
      const glitchX = (d > 0 && d <= gridDepth && rowGlitch[d - 1]) ? rowGlitch[d - 1].offsetX : 0;
      const leftX = vpX - lineSpread / 2 + wobbleOffset + glitchX;
      const rightX = vpX + lineSpread / 2 + wobbleOffset + glitchX;

      ctx.beginPath();
      ctx.moveTo(leftX, y);
      ctx.lineTo(rightX, y);
      ctx.stroke();
    }

    // Vertical lines: lerp top endpoint between VP (perspective) and straight up (flat)
    for (let lane = 0; lane <= laneCount; lane++) {
      const laneT = lane / laneCount;
      // Bottom point (always full width)
      const bx = vpX + (laneT - 0.5) * spread + wobbleOffset;
      // Top point: flat = same X (vertical), perspective = converges to VP
      const topX = bx + ((vpX + wobbleOffset) - bx) * p;
      ctx.beginPath();
      ctx.moveTo(bx, bottomY);
      ctx.lineTo(topX, vpY);
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Draw a block with chromatic aberration (RGB channel split).
   * Uses source-over compositing to avoid additive blowout through trail fade.
   * The visual channel separation is achieved by spatial offset, not additive mixing.
   */
  _drawChromaticBlock(ctx, x, y, w, h, laneT, dist, amp) {
    const { r, g, b } = this._laneColorChannels(laneT);
    const alpha = 0.45 + amp * 0.3;

    ctx.save();
    ctx.shadowBlur = 0; // Disable glow for chroma blocks to avoid triple-glow

    // Red channel — offset left
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `rgb(${r},${Math.round(g * 0.15)},${Math.round(b * 0.15)})`;
    ctx.fillRect(x - dist, y, w, h);

    // Green channel — center
    ctx.fillStyle = `rgb(${Math.round(r * 0.15)},${g},${Math.round(b * 0.15)})`;
    ctx.fillRect(x, y, w, h);

    // Blue channel — offset right
    ctx.fillStyle = `rgb(${Math.round(r * 0.15)},${Math.round(g * 0.15)},${b})`;
    ctx.fillRect(x + dist, y, w, h);

    ctx.restore();
  }

  /**
   * Clear canvas and reset all runtime state.
   */
  clear() {
    super.clear();
    this._isFirstFrame = true;
    this._wobblePhase = 0;
    this._initRowGlitch();
    // Clear trail buffer so stale frames don't flash on restart
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(GlitchPrismGrid);

export { GlitchPrismGrid };
export default GlitchPrismGrid;
